// QRCodeGenerator.js
import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../components/firebase-config.js";
import { v4 as uuidv4 } from 'uuid';
import styled from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faDownload, faQrcode } from "@fortawesome/free-solid-svg-icons";

const QRCodeContainer = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const QRCodeWrapper = styled.div`
  padding: 1.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin: 2rem 0;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  background: linear-gradient(-45deg, #6366f1, #8b5cf6, #3b82f6);
  color: white;
  border: none;
  font-weight: 600;
  margin: 0.5rem;
  cursor: pointer;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const QRCodeGenerator = ({ auxiliaireData, baseUrl }) => {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  
  useEffect(() => {
    if (auxiliaireData) {
      generateQRCode();
    }
  }, [auxiliaireData]);
  
  const generateQRCode = async () => {
    // Générer un identifiant unique pour ce code QR
    const qrId = uuidv4();
    
    // Créer les données du code QR
    const qrData = {
      auxiliaireId: auxiliaireData.id,
      structureId: auxiliaireData.structureId,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000)), // Expire après 24h
      medecinIds: auxiliaireData.medecinIds || [],
      active: true
    };
    
    // Enregistrer les données dans Firestore
    await setDoc(doc(db, "qrCodes", qrId), qrData);
    
    setQrCodeData(qrData);
    
    // Créer l'URL qui sera encodée dans le QR code
    const url = `http://localhost:3000/rendez-vous?qr=${qrId}`;
    setQrCodeUrl(url);
  };
  
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const pngUrl = canvas
        .toDataURL("image/png")
        .replace("image/png", "image/octet-stream");
      
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `qrcode-${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
  
  const copyQRCodeUrl = () => {
    navigator.clipboard.writeText(qrCodeUrl);
    alert("URL copiée dans le presse-papiers!");
  };
  
  if (!qrCodeUrl) {
    return <div>Chargement du code QR...</div>;
  }
  
  return (
    <QRCodeContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h4>Code QR pour la prise de rendez-vous</h4>
      <p>Les patients peuvent scanner ce code pour prendre rendez-vous directement.</p>
      
      <QRCodeWrapper>
        <QRCodeSVG 
          id="qr-code-canvas"
          value={qrCodeUrl} 
          size={250}
          level="H"
          includeMargin={true}
          imageSettings={{
            src: "/logo-samd.png",
            height: 50,
            width: 50,
            excavate: true,
          }}
        />
      </QRCodeWrapper>
      
      <div className="d-flex flex-wrap justify-content-center">
        <ActionButton 
          onClick={downloadQRCode}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faDownload} />
          Télécharger
        </ActionButton>
        
        <ActionButton 
          onClick={copyQRCodeUrl}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faCopy} />
          Copier le lien
        </ActionButton>
        
        <ActionButton 
          onClick={generateQRCode}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faQrcode} />
          Générer un nouveau code
        </ActionButton>
      </div>
    </QRCodeContainer>
  );
};

export default QRCodeGenerator;

